import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { deriveReturnTimeFraction } from '../../utils/tickCountdown';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { NumericInput } from '../strategy/NumericInput';
import { DraftSettingsPanel } from './DraftSettingsPanel';
import { ReleaseTable } from './ReleaseTable';
import { TrainingOverviewTab } from './TrainingOverviewTab';
import { TRAINABLE_UNITS } from '../../constants/trainableUnits';
import { computeUnitDeficit } from '../../utils/unitStockMath';
import { aggregateQueueBatches } from '../../utils/queueAggregation';
import { QueueSummaryRow } from '../shared/QueueSummaryRow';
import type { TrainingShellProps, TrainingTab } from '../../pages/Training';

/** Matches Layout.tsx/AppShellLayout.tsx's existing reserve for MobileTabBar's footprint (same
 *  precedent as MobileBuildingsShell/MobileExploreShell — no shared named constant exists yet). */
const TAB_BAR_OFFSET = '96px';

// SF-162: Overview placed last, matching MobileSciencesShell's Invest-then-Overview ordering
// precedent (Component 4).
const TABS: readonly { key: TrainingTab; label: string }[] = [
  { key: 'train', label: 'Train' },
  { key: 'draft', label: 'Draft' },
  { key: 'release', label: 'Release' },
  { key: 'overview', label: 'Overview' },
];

export function MobileTrainingShell({
  province,
  activeStrategy,
  strategyLoading,
  mobileTab,
  setMobileTab,
  trainQty,
  accelerated,
  setAccelerated,
  isSubmitting,
  bulkMutationVersion,
  releaseQty,
  setReleaseQty,
  draftRate,
  setDraftRate,
  draftTarget,
  setDraftTarget,
  isSubmittingOptions,
  optionsError,
  optionsSuccess,
  draftLoadMessage,
  hasDraftUnsavedChanges,
  raceConfig,
  draftRates,
  maxDraftTarget,
  draftCapacityPop,
  goldAvailable,
  soldiers,
  peasants,
  draftPct,
  draftTargetCount,
  trainingTime,
  totalCost,
  totalTraining,
  getTarget,
  getTargetOrNull,
  getOwnedCount,
  getInTrainingCount,
  getTrainingCost,
  getUnitName,
  getUnitStats,
  handleTrainInputChange,
  handleOptimizeAll,
  handleOptimizeSingleUnit,
  handleClearAll,
  handleTrainSubmit,
  handleLoadDraftFromStrategy,
  handleDraftSave,
  handleReleaseBulkMutation,
  worldId,
  provinceId,
  refresh,
}: TrainingShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const [expandedTrainingUnit, setExpandedTrainingUnit] = useState<string | null>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const returnTimeFraction = deriveReturnTimeFraction(province.sync);

  const bottomBarVisible = mobileTab === 'train' && totalTraining > 0;

  // SF-179 Component 5: collapse the raw per-tick-batch "In Training" list (one row per remaining
  // tick, up to 24+ rows per unit type) to one aggregated row per unit type. max(ticksRemaining) is
  // correct here (not min) — Training's queue is drip-distributed one-batch-per-tick, so
  // min(ticksRemaining) across surviving batches is pinned to a constant and carries no
  // information (SF-140/SF-170 precedent); max is "days until fully drained."
  const trainingUnitOrder = TRAINABLE_UNITS.map((u) => u.type);
  const aggregatedTraining = aggregateQueueBatches(province.military.training, {
    groupKey: (t) => t.unitType,
    ticksRemaining: (t) => t.ticksRemaining,
    amount: (t) => t.unitCount,
    compare: (a, b) => trainingUnitOrder.indexOf(a as (typeof trainingUnitOrder)[number]) - trainingUnitOrder.indexOf(b as (typeof trainingUnitOrder)[number]),
  });

  return (
    <div className="space-y-3" style={bottomBarVisible ? { paddingBottom: '110px' } : undefined}>
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        Military
      </h1>

      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="military" compact />

      <div className="flex border-b overflow-x-auto" style={{ borderColor: COLORS.border }} role="tablist">
        {TABS.map((tab) => {
          const isActive = mobileTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`training-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`training-panel-${tab.key}`}
              onClick={() => setMobileTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap"
              style={{
                minHeight: 44,
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
              {tab.key === 'draft' && hasDraftUnsavedChanges && (
                <span
                  aria-label="Unsaved changes"
                  className="rounded-full"
                  style={{ width: 6, height: 6, backgroundColor: COLORS.gold, display: 'inline-block' }}
                />
              )}
            </button>
          );
        })}
      </div>

      {mobileTab === 'train' && (
        <div role="tabpanel" id="training-panel-train" aria-labelledby="training-tab-train" className="flex flex-col gap-3">
          {/* SF-162: renamed from "Overview" — the new Overview tab now owns that name unambiguously
              (naming collision found during mockup review, Component 4). */}
          <GlanceCard title="At a Glance">
            <MetricRow label="Gold" unit="gc" value={formatNumber(goldAvailable)} weight="primary" />
            <MetricRow label="Soldiers" unit="" value={formatNumber(soldiers)} weight="secondary" />
            <MetricRow label="Peasants" unit="" value={formatNumber(peasants)} weight="secondary" />
            <MetricRow label="Training Time" unit="days" value={trainingTime} weight="tertiary" />
          </GlanceCard>

          {aggregatedTraining.length > 0 && (
            <GlanceCard title="In Training">
              {aggregatedTraining.map((group) => {
                const unitDef = TRAINABLE_UNITS.find((u) => u.type === group.groupKey);
                const name = getUnitName(group.groupKey, unitDef?.key ?? null);
                const time = formatDuration(group.maxTicksRemaining, {
                  returnTimeFraction,
                  tickIntervalMs: province.sync.tickIntervalMs,
                });
                const expanded = expandedTrainingUnit === group.groupKey;
                const rawBatches = province.military.training.filter((t) => t.unitType === group.groupKey);

                return (
                  <QueueSummaryRow
                    key={group.groupKey}
                    id={`in-training-${group.groupKey}`}
                    label={name}
                    delta={`+${formatNumber(group.total)}`}
                    timeLabel={time}
                    expanded={expanded}
                    onToggle={() => setExpandedTrainingUnit(expanded ? null : group.groupKey)}
                  >
                    {rawBatches.map((t, idx) => {
                      const batchTime = formatDuration(t.ticksRemaining, {
                        returnTimeFraction,
                        tickIntervalMs: province.sync.tickIntervalMs,
                      });
                      return (
                        <div key={`${t.unitType}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                          <span style={{ color: COLORS.text.primary }}>
                            {name} <span style={{ color: COLORS.gold }}>+{formatNumber(Math.floor(t.unitCount))}</span>
                          </span>
                          <span className="font-mono text-[11px]" style={{ color: COLORS.text.tertiary }}>{batchTime}</span>
                        </div>
                      );
                    })}
                  </QueueSummaryRow>
                );
              })}
            </GlanceCard>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleOptimizeAll}
              disabled={!activeStrategy?.settings}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 rounded-lg"
              style={{
                minHeight: 44,
                backgroundColor: activeStrategy?.settings ? 'rgba(212,167,44,0.08)' : 'transparent',
                border: `1px solid ${activeStrategy?.settings ? 'rgba(212,167,44,0.35)' : COLORS.border}`,
                color: activeStrategy?.settings ? COLORS.gold : COLORS.text.tertiary,
                cursor: activeStrategy?.settings ? 'pointer' : 'default',
              }}
            >
              ⚡ Fill Strategy
            </button>
            <label
              className="ml-auto flex items-center gap-1.5 text-xs"
              style={{ minHeight: 44, color: COLORS.text.tertiary }}
            >
              <input
                type="checkbox"
                checked={accelerated}
                onChange={(e) => setAccelerated(e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: COLORS.gold }}
              />
              Accelerated (2× cost, ½ time)
            </label>
          </div>

          {!activeStrategy?.settings && (
            <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
              No active strategy —{' '}
              <Link to="/strategy" style={{ color: COLORS.link }}>
                set one to see target guidance
              </Link>
              .
            </div>
          )}

          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
            {TRAINABLE_UNITS.map((unit, rowIndex) => {
              const owned = getOwnedCount(unit.type);
              const inTraining = Math.floor(getInTrainingCount(unit.type));
              const cost = getTrainingCost(unit.type);
              const displayCost = accelerated ? cost * 2 : cost;
              const stats = getUnitStats(unit.type, unit.key);
              const unitName = getUnitName(unit.type, unit.key);
              const qty = trainQty[unit.type] || 0;

              const target = getTarget(unit.type);
              const hasTarget = target > 0;
              const { deficit } = computeUnitDeficit({ target, owned, inProgress: inTraining });
              const maxByGold = displayCost > 0 ? Math.floor(goldAvailable / displayCost) : 0;
              const canOptimize = hasTarget && deficit > 0 && maxByGold > 0 &&
                (unit.type === 'THIEF' ? peasants > 0 : soldiers > 0);

              // SF-179 Component 6 (mobile-only): row 2 merges cost + in-prog into the pre-existing
              // target/deficit context. The no-target branch is left as the bare, unmerged "No target
              // set" string — unchanged from before this component, matching the existing KEEP test.
              let row2: string;
              let row2Color: string = COLORS.text.tertiary;
              const inProgLabel = inTraining > 0 ? formatNumber(inTraining) : '—';
              if (!hasTarget) {
                row2 = 'No target set';
              } else if (deficit <= 0) {
                row2 = `${formatNumber(displayCost)} gc · ${inProgLabel} in prog · ✓ At target (${formatNumber(target)})`;
                row2Color = COLORS.positive;
              } else {
                row2 = `${formatNumber(displayCost)} gc · ${inProgLabel} in prog · deficit ${formatNumber(deficit)} of ${formatNumber(target)} target`;
              }

              return (
                <div
                  key={unit.type}
                  className="flex flex-col gap-1.5 px-3 py-2.5"
                  style={{ borderBottom: rowIndex < TRAINABLE_UNITS.length - 1 ? `1px solid ${COLORS.border}` : undefined }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate min-w-0 flex-1" style={{ color: COLORS.text.primary }}>
                      {unitName} ({stats.offense}/{stats.defense})
                    </span>
                    <span className="text-sm font-mono flex-shrink-0" style={{ color: COLORS.text.primary }}>
                      {formatNumber(owned)}
                    </span>
                    <NumericInput
                      value={qty}
                      onChange={(v) => handleTrainInputChange(unit.type, String(v))}
                      size="touch"
                      resetKey={bulkMutationVersion}
                      className="w-20"
                      ariaLabel={`Train ${unitName} quantity`}
                    />
                    {canOptimize && (
                      <button
                        onClick={() => handleOptimizeSingleUnit(unit.type)}
                        aria-label={`Optimize ${unitName} to target`}
                        style={{
                          width: 44,
                          height: 44,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          border: `1px solid ${COLORS.border}`,
                          backgroundColor: COLORS.sectionHeader,
                          color: '#B89878',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        ⚡
                      </button>
                    )}
                  </div>
                  <div className="text-[11px]" style={{ color: row2Color }}>
                    {row2}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mobileTab === 'draft' && (
        <div role="tabpanel" id="training-panel-draft" aria-labelledby="training-tab-draft" className="flex flex-col gap-3">
          <GlanceCard title="Draft Level">
            <MetricRow label="Current" unit="%" value={draftPct} weight="primary" />
            <MetricRow label="Soldiers / Target" unit="" value={`${formatNumber(soldiers)} / ${formatNumber(draftTargetCount)}`} weight="secondary" />
            {/* SF-179 Component 4: unconditional caption — the Economy page's Total uses a
                narrower population.total, so this must read as intentional, not a mismatch. */}
            <div className="text-[10px]" style={{ color: COLORS.text.tertiary }}>
              of {formatNumber(draftCapacityPop)} total army capacity
            </div>
          </GlanceCard>

          <DraftSettingsPanel
            draftRate={draftRate}
            draftTarget={draftTarget}
            draftRates={draftRates}
            maxDraftTarget={maxDraftTarget}
            isSubmitting={isSubmittingOptions}
            error={optionsError}
            success={optionsSuccess}
            onDraftRateChange={setDraftRate}
            onDraftTargetChange={setDraftTarget}
            onSave={handleDraftSave}
            activeStrategy={activeStrategy}
            onLoadFromStrategy={handleLoadDraftFromStrategy}
            draftLoadMessage={draftLoadMessage}
            compact
          />
        </div>
      )}

      {mobileTab === 'release' && (
        <div role="tabpanel" id="training-panel-release" aria-labelledby="training-tab-release">
          <ReleaseTable
            province={province}
            raceConfig={raceConfig}
            worldId={worldId ?? ''}
            provinceId={provinceId ?? ''}
            releaseQty={releaseQty}
            onReleaseQtyChange={setReleaseQty}
            onSuccess={refresh}
            activeStrategy={activeStrategy}
            getTargetOrNull={getTargetOrNull}
            resetKey={bulkMutationVersion}
            onBulkMutation={handleReleaseBulkMutation}
            compact
          />
        </div>
      )}

      {mobileTab === 'overview' && (
        <div role="tabpanel" id="training-panel-overview" aria-labelledby="training-tab-overview">
          <TrainingOverviewTab province={province} getUnitStats={getUnitStats} getUnitName={getUnitName} compact />
        </div>
      )}

      {bottomBarVisible && (
        <div
          data-testid="train-bottom-bar"
          className="fixed left-0 right-0 flex flex-col gap-2"
          style={{
            bottom: TAB_BAR_OFFSET,
            backgroundColor: '#0b0e19',
            borderTop: `1px solid ${COLORS.border}`,
            padding: '10px 14px',
            paddingBottom: `calc(10px + env(safe-area-inset-bottom))`,
            zIndex: 30,
          }}
        >
          <div className="flex items-baseline justify-between text-[11px]" style={{ color: COLORS.text.tertiary }}>
            <span>
              Training{' '}
              <b className="font-mono" style={{ color: COLORS.text.primary }}>
                {formatNumber(totalTraining)} units
              </b>
            </span>
            <span className="font-mono font-bold" style={{ color: COLORS.text.primary }}>
              {formatNumber(totalCost)} gc · {trainingTime} days
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClearAll}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text.secondary, minHeight: 44 }}
            >
              Clear
            </button>
            <button
              onClick={handleTrainSubmit}
              disabled={isSubmitting}
              className="flex-1 text-center py-2.5 rounded-lg text-sm font-bold"
              style={{
                backgroundColor: COLORS.btnPrimary,
                border: `1px solid ${COLORS.btnPrimaryHover}`,
                color: COLORS.text.primary,
                minHeight: 44,
                opacity: isSubmitting ? 0.5 : 1,
              }}
            >
              Train Units
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
